import { pick, clone } from '@/common/js/just.js'
import { getWxgamePlayablePage, getWxgamePlayableComponent } from './creative-form.js'
import { budgetForm, deepConversionBehavior, deepConversionRoi } from './budget/budget-form.js'
import { rebuildBidAdjustmentForm } from './budget/bid-adjustment.js'
import {
  DEEP_CONVERSION_TYPE,
  COST_CONTROL,
  SEARCH_EXPAND_TARGETING_SWITCH,
  SEARCH_EXPANSION_SWITCH,
  SMART_DELIVERY_GOAL
} from './budget/budget-const.js'
import { MANUAL_SITES, SITE_ENUM, EXPLORATION_ENUM } from './siteset/siteset-const.js'
import { BRAND_ENUM, LINK_NAME_TYPE, PAGE_TYPES, DYNAMIC_CREATIVE_TYPE } from './creative-const.js'
import {
  PROMOTED_TYPE,
  MARKETING_TYPE,
  MARKETING_SUB_GOAL,
  MARKETING_CARRIER_TYPE,
  PROMOTED_TYPE_BY_PLATFORM,
  PLATFORM,
  AD_MARKETING_SCENE,
  SMART_DELIVERY_PLATFORM,
  SEARCH_AD_BID_KEYWORD_PATERN
} from './project-const.js'

import { AGES, CONVERSION_BEHAVIOR, EXCLUDED_DAY } from './targeting-const.js'
import { dataFormatter, externalDataFormatter } from '@/components/material-selector/utils.js'
import { queryImageDetailByIds } from '@/api/common.model'

const normalizeCreativeMaterialId = (id) => String(id ?? '').replace(/^@/, '')

const getCreativeMaterialComponents = (creativeTemplateId, creativeComponents = {}) => {
  const carouselImageComponent =
    creativeComponents.image_list?.find((item) => item.value?.display_mode === 'CAROUSEL') ||
    (creativeTemplateId === 2688 ? creativeComponents.image_list?.[0] : null)
  const carouselImageList = carouselImageComponent?.value?.list || []
  const carouselVideoList = creativeComponents.video_list?.[0]?.value?.list || []
  const isCarouselThreeImage = creativeTemplateId === 2688 || carouselImageList.length > 0
  const isCarouselThreeVideo = creativeTemplateId === 2888 || carouselVideoList.length > 0

  return {
    ...creativeComponents,
    image: isCarouselThreeImage ? carouselImageList.map((value) => ({ value })) : creativeComponents.image,
    video: isCarouselThreeVideo ? carouselVideoList.map((value) => ({ value })) : creativeComponents.video,
    // 轮播三图的 image_list 是图片列表，不是项目原有的组图素材
    image_list: isCarouselThreeImage ? [] : creativeComponents.image_list
  }
}

function getTargetPopulations(accounts) {
  const populations = {}

  if (accounts?.length) {
    accounts.forEach((acc) => {
      populations[acc.id] = {
        accountId: acc.id,
        includes: [],
        excludes: []
      }
    })
  }

  return populations
}

/**
 * @description: 根据已有参数构造表单或者返回默认值的表单，用于批量新建项目流程
 * @param {Number} index
 * @param {Object} existForm
 * @return {Object}
 */
export function getTargetingForm(index = 0, existForm = {}) {
  const populations = getTargetPopulations(this.adAccounts)
  return {
    name: '定向包' + (index + 1),
    audienceType: 0, // 人群包类型
    regionType: 0,
    region: [], // 地域
    locationTypes: ['LIVE_IN'],
    gender: 0, // 性别
    age: 0, // 年龄
    network: [0], // 网络
    game_consumption_level: [0], // 游戏用户消费能力
    wechatAdBehaviorType: [0], // 微信再营销
    wechatAdBehavior: {
      actions: [], // 微信再营销包含行为列表
      excluded_actions: [] // 微信再营销排除行为列表
    },
    ageRange: [19, 25], // 自定义年龄段
    deviceType: 0, // 手机品牌类型
    device: [], // 手机品牌
    excludeDevice: false, // 排除所选设备
    osType: 0,
    version: [], // 系统版本
    devicePrice: [0], // 设备价格

    // 排除已转化用户
    excludeCustom: false,
    excluded_converted_audience: {
      excluded_dimension: 0,
      conversion_behavior_list: [CONVERSION_BEHAVIOR.APP_ACTIVATE],
      excluded_day: EXCLUDED_DAY.ONE_MONTH
    },
    ...existForm,
    populations // 人群包
  }
}

/**
 * @description: 获取投放计划表单
 * @return {*}
 */
export function getPlanForm() {
  const project_name = `项目名称_${new Date().getTime()}`
  return {
    accounts: [], // 已选账号id
    project_name,
    ch_platform_id: 1,
    game_id: {}, // 投放应用信息
    callback_switch: false, // 数据上报开关
    monitorPlan: [], // 智能自动化

    automatic_site_enabled: false, // 手动版位
    siteSet: MANUAL_SITES,

    promotedObject: {}, // 推广目标id信息对象 { promoted_object_id: 0, promoted_object_name: '', ...}

    // 微信公众号与小程序定投
    wechat_position: 0,
    wechatPostion: [],
    // 微信视频号定投
    wechat_channels: 0,
    wechatChannels: [],
    // 微信公众号与小程序场景
    wechat_scene: 0,
    // 公众号媒体类型
    officialAccountMediaCate: [],
    miniGame: [], // 小程序小游戏流量 checkbox model
    payScene: [], // 订单详情页消费场景 checkbox model

    wechat_search_scene: 0,
    wechatSearchScene: [],
    qbsearch_scene: 0,
    qbSearchScene: [],

    // 优量汇场景
    displaySceneType: 0, // 优量汇广告展示场景
    excludeUnionType: 0, // 优量汇流量包屏蔽
    flow_optimization_enabled: false, // 优量汇自动流量优选
    scene_spec: {
      display_scene: [],
      // 分帐号保存的已选优量汇流量包屏蔽包
      // {account_id: 0, excludePackages: []}
      exclude_union_position_package: {}
    }
  }
}

/**
 * @description: 获取ada投放计划表单
 * @return {*}
 */
export function getADAPlanForm() {
  return {
    ...getPlanForm(),
    marketing_scene: 'DEFAULT',
    promoted_object_type: PROMOTED_TYPE.APP_ANDROID,
    speed_mode: 'SPEED_MODE_STANDARD', // 投放方式
    promotedObject: {} // 推广
  }
}

/**
 * @description: 获取v3投放计划表单
 * @return {*}
 */
export function getV3PlanForm() {
  const project_name = `计划名称_${new Date().getTime()}`
  return {
    ...getPlanForm(),
    siteSet: MANUAL_SITES,
    project_name,

    ad_marketing_scene: AD_MARKETING_SCENE.VERSION_3,
    smart_targeting_mode: false, // 定向模式：false=智能定向，true=手动定向
    smart_delivery_platform: SMART_DELIVERY_PLATFORM.MINI_GAME_PROMOTION,

    marketing_goal: MARKETING_TYPE.USER_GROWTH, // 营销目的
    marketing_sub_goal: MARKETING_SUB_GOAL.PLATEAU_PHASE_LAUNCH, // 子目的

    marketing_carrier_type: MARKETING_CARRIER_TYPE.APP_ANDROID, // 营销对象
    marketing_carrier_detail: {
      // 营销对象详情
      marketing_carrier_id: ''
    },

    budget: 0,
    daily_budget: ''
  }
}

/**
 * @description: 投放素材标签
 * @return {*}
 */
export function getMaterialTagForm() {
  return {
    name: '',
    l1: '',
    l2: ''
  }
}

/**
 * @description: 根据任务参数回显项目，用于批量复制、批量修改流程
 * @param {*} taskInfo
 */
export async function getModifyPlanForm(taskInfo) {
  const { taskId, task_progress, task_status, convert_name, ad_account_name, ad_account_id, batch_task_type, operation } = taskInfo

  const planForm = {
    taskId,
    task_progress,
    task_status,
    campaign_type: 0,
    convert_name,
    batch_task_type,
    ad_account_name,
    ad_account_id,
    operation,
    planForm: {
      // 项目默认参数
      ...getPlanForm(),
      // 根据任务参数构造的项目参数
      ...rebuildPlanForm.call(this, taskInfo)
    },
    budgetForm: {
      ...budgetForm,
      // 根据任务参数构造的项目出价参数
      ...rebuildPlanBudgetForm(taskInfo)
    },
    targetingForm: {
      // 默认定向参数
      ...getTargetingForm.call(this),
      // 根据任务参数构造的定向参数
      ...rebuildTargetingForm(taskInfo)
    },
    creativeForm: await rebuildCreativeForm.call(this, taskInfo)
  }

  return planForm
}

/**
 * @description: 重新构造用于批量失败修改、批量失败任务的数据结构
 * @param {*} taskInfo
 */
function rebuildPlanForm(taskInfo) {
  const { inner_plan_params, outer_plan_params, ad_account_id, convert_name } = taskInfo
  const { plan_name, game_id, ch_platform_id, callback_switch } = inner_plan_params
  let {
    marketing_scene,
    ad_marketing_scene = AD_MARKETING_SCENE.VERSION_3,
    automatic_site_enabled,
    site_set = MANUAL_SITES,
    promoted_object_id,
    promoted_object_type,
    flow_optimization_enabled,
    scene_spec,
    exploration_strategy = EXPLORATION_ENUM.AUTOMATIC,
    priority_site_set = MANUAL_SITES,
    smart_delivery_platform = SMART_DELIVERY_PLATFORM.MINI_GAME_PROMOTION
  } = outer_plan_params
  if (!promoted_object_type) promoted_object_type = PROMOTED_TYPE_BY_PLATFORM[ch_platform_id]

  const project_name = this.pageStates.isDuplicate ? plan_name.concat('_复制') : plan_name

  const planForm = {
    project_name,
    // 保存的原始项目名称
    originProjectName: convert_name,
    // account: ad_account_id,
    accounts: [ad_account_id],
    callback_switch,
    ch_platform_id,
    game_id: {
      id: game_id
    },
    marketing_scene,
    ad_marketing_scene,
    siteSet: site_set,
    exploration_strategy,
    priority_site_set,
    promoted_object_type,
    automatic_site_enabled,
    flow_optimization_enabled,
    promotedObject: {
      promoted_object_id,
      promoted_object_type
    },
    smart_delivery_platform
  }
  const {
    wechat_position,
    // 微信视频号定投
    wechat_channels_scene,
    // 微信场景定向
    wechat_scene,
    // 优量汇广告展示场景
    display_scene,
    // 优量汇流量包屏蔽
    exclude_union_position_package,
    // PC端定投
    pc_scene,

    // 搜索广告qq浏览器，微信搜一搜场景流量
    qbsearch_scene,
    wechat_search_scene
  } = scene_spec

  if (wechat_channels_scene) {
    planForm.wechat_channels = 1
    planForm.wechatChannels = wechat_channels_scene
  }

  if (wechat_position) {
    planForm.wechat_position = 1
    planForm.wechatPostion = wechat_position
  }
  if (wechat_scene) {
    const { official_account_media_category, mini_program_and_mini_game, pay_scene } = wechat_scene
    planForm.wechat_scene = 1
    planForm.officialAccountMediaCate = official_account_media_category
    planForm.miniGame = mini_program_and_mini_game
    planForm.payScene = pay_scene
  }

  if (display_scene) {
    const scene_spec = {}
    scene_spec.display_scene = display_scene
    planForm.displaySceneType = 1
  } else {
    scene_spec.display_scene = []
  }

  if (exclude_union_position_package?.length) {
    planForm.excludeUnionType = 1
    scene_spec.exclude_union_position_package = {
      [ad_account_id]: exclude_union_position_package
    }
  } else {
    // 设置默认结构
    planForm.excludeUnionType = 0
    scene_spec.exclude_union_position_package = {}
  }

  if (pc_scene) {
    planForm.pc_scene = 1
    planForm.pcScene = pc_scene
  } else {
    planForm.pc_scene = 0
    planForm.pcScene = []
  }

  if (qbsearch_scene) {
    planForm.qbsearch_scene = 1
    planForm.qbSearchScene = qbsearch_scene
  } else {
    planForm.qbsearch_scene = 0
    planForm.qbSearchScene = []
  }

  if (wechat_search_scene) {
    planForm.wechat_search_scene = 1
    planForm.wechatSearchScene = wechat_search_scene
  } else {
    planForm.wechat_search_scene = 0
    planForm.wechatSearchScene = []
  }

  planForm.scene_spec = scene_spec

  return planForm
}

function rebuildSearchAdPlanForm(taskInfo) {
  const { outer_plan_params, inner_plan_params, campaign_params } = taskInfo
  const { daily_budget } = campaign_params
  const { marketing_scene, ...planform } = rebuildPlanForm.call(this, taskInfo)
  const { promoted_object_id, searchad_bidword_list } = outer_plan_params
  const { ch_platform_id } = inner_plan_params
  planform.promotedObject.promoted_object_id = promoted_object_id
  planform.game_id.gdtAppid = promoted_object_id
  // 根据平台把搜索广告的marketing_carrier_type回显
  switch (ch_platform_id) {
    case PLATFORM.ANDROID:
      planform.marketing_carrier_type = MARKETING_CARRIER_TYPE.APP_ANDROID
      break
    case PLATFORM.IOS:
      planform.marketing_carrier_type = MARKETING_CARRIER_TYPE.APP_IOS
      break
    case PLATFORM.MINIGAME:
      planform.marketing_carrier_type = MARKETING_CARRIER_TYPE.MINI_GAME
      break
    default:
      break
  }

  planform.searchAdBidKeywords = searchad_bidword_list.map((w) => {
    const patern = SEARCH_AD_BID_KEYWORD_PATERN.find((p) => p.value === w.match_type) || SEARCH_AD_BID_KEYWORD_PATERN[0]
    return { keyword: w.bidword, msv: '', patern }
  })

  if (daily_budget && Number(daily_budget) > 0) {
    planform.budget = 1
    planform.daily_budget = daily_budget
  }
  console.log('planformplanformplanformplanform output: ', planform)
  return planform
}

/**
 * @description: 腾讯广告3.0外部计划参数
 * @param {*} taskInfo
 */
function rebuildV3PlanForm(taskInfo) {
  const { outer_plan_params } = taskInfo
  const { marketing_carrier_detail, marketing_carrier_type, marketing_goal, marketing_sub_goal, smart_targeting_mode } = outer_plan_params
  const { marketing_scene, ...planform } = rebuildPlanForm.call(this, taskInfo)

  planform.marketing_carrier_detail = marketing_carrier_detail
  planform.marketing_carrier_type = marketing_carrier_type
  planform.marketing_goal = marketing_goal
  planform.marketing_sub_goal = marketing_sub_goal
  planform.promotedObject.promoted_object_id = marketing_carrier_detail.marketing_carrier_id
  // 默认回显时所选投放应用与app一致
  planform.game_id = { id: planform.game_id.id, gdtAppid: marketing_carrier_detail.marketing_carrier_id }
  // 定向模式回显
  planform.smart_targeting_mode = smart_targeting_mode === 'SMART_TARGETING_MANUAL'
  return planform
}
/**
 * @description: 腾讯广告3.0计划出价参数
 * @param {*} taskInfo
 */
function rebuildV3PlanBudgetForm(taskInfo, options) {
  const { ...bidform } = rebuildPlanBudgetForm(taskInfo, options)
  return bidform
}

/**
 * @description: 腾讯广告搜索广告创意参数
 * @param {*} taskInfo
 * @return {*}
 */
async function rebuildSearchAdCreativeForm(taskInfo) {
  const { ch_platform_id } = taskInfo.inner_plan_params
  const { dynamic_creative_template_id, dynamic_creative_name, dynamic_creative_elements, materials } = taskInfo.creative_params
  const {
    description_component_options,
    title_component_options,
    landing_page_component_options,
    brand_component_options,
    video_component_options,
    image_component_options = [],
    image3_component_options = [],
    image_list_component_options
  } = dynamic_creative_elements

  const wechat_channels = {
    finder_object_visibility: false,
    channelInfo: {}
  }
  const brand = {
    brandInfo: { image_id: '', name: '' },
    profileInfo: { brand_image_id: '', profile_id: '', profile_name: '' },
    officialAccountInfo: { promoted_object_id: '' }
  }
  const [brandItem] = brand_component_options || []
  if (brandItem?.value) {
    const { brand_img, brand_name } = brandItem.value || {}
    // 品牌形象
    brand.brandInfo.name = brand_name
    brand.brandInfo.image_id = brand_img.image_id
    brand.brandType = BRAND_ENUM.BRAND
  }

  const pageInfo = landing_page_component_options.map((item) => {
    const { page_spec } = item.value[0] || {}
    const { page_id } = page_spec || {}
    let pageType = ''

    // 安卓官方落地页
    if (PLATFORM.ANDROID === ch_platform_id && page_id) pageType = 5

    if (PLATFORM.IOS === ch_platform_id) {
      if (page_id) {
        // IOS官方落地页
        pageType = 5
      } else {
        // IOS APP STORE
        pageType = 3
      }
    }

    if (PLATFORM.MINIGAME === ch_platform_id) {
      pageType = 4
    }

    return {
      page: { page_id: page_id ? String(page_id) : '' },
      pageType
    }
  })

  const creativeForm = {
    groupName: dynamic_creative_name,
    automatic_site_enabled: false,
    dynamic_creative_name: dynamic_creative_name,

    // 搜索广告没有dynamic_creative_type，为了显示素材默认给一个
    dynamic_creative_type: DYNAMIC_CREATIVE_TYPE.PROGRAM,
    creative_template_id: dynamic_creative_template_id,
    selectedAssets: {},
    description: title_component_options?.map((desc) => ({ content: desc.value })) || [],
    description_component_options,
    components: {
      brand,
      wechat_channels
    },
    pageInfo
  }

  const creative_components = {
    // 数据结构进行转换用来兼容v3版本的处理方法
    video: video_component_options?.map((v) => {
      return {
        value: {
          video_id: v.value.video.video_id,
          cover_id: v.value.cover_image.image_id
        }
      }
    }),
    image: [...image_component_options, ...image3_component_options],
    image_list: image_list_component_options
  }

  const { video, image, image_list } = await getCreativeAssets(creative_components, materials)
  if (video) creativeForm.selectedAssets.video = video
  if (image) creativeForm.selectedAssets.image = image
  if (image_list) creativeForm.selectedAssets.image_list = image_list

  return [creativeForm]
}

async function rebuildV3CreativeForm(taskInfo) {
  // material_info 只有单条计划修改时会存在
  const { inner_plan_params, creative_params, material_info } = taskInfo
  const { ch_platform_id } = inner_plan_params

  const { delivery_mode, dynamic_creative_type, ideaName, dynamic_creative_name, creative_template_id, creative_components, materials } =
    creative_params
  const { description } = creative_components || {}
  const materialCreativeComponents = getCreativeMaterialComponents(creative_template_id, creative_components)

  const [brandItem] = creative_components.brand || []
  const [wechatChannels] = creative_components.wechat_channels || []

  const wechat_channels = {
    finder_object_visibility: false,
    channelInfo: {}
  }

  const brand = {
    brandInfo: { image_id: '', name: '' },
    profileInfo: { brand_image_id: '', profile_id: '', profile_name: '' },
    officialAccountInfo: { promoted_object_id: '' }
  }

  // 品牌形象跳转
  // 视频号
  if (wechatChannels) {
    brand.brandType = BRAND_ENUM.CHANNEL
    wechat_channels.finder_object_visibility = wechatChannels.value.finder_object_visibility
    wechat_channels.channelInfo = {
      promoted_object_id: wechatChannels.value.username
    }
  } else {
    if (brandItem) {
      const { page_type, page_spec } = brandItem.value.jump_info || {}
      if (page_type === 'PAGE_TYPE_H5_PROFILE') {
        // 品牌简介页
        brand.profileInfo.profile_id = page_spec.h5_profile_spec?.page_id
        brand.profileInfo.profile_name = brandItem.value.brand_name
        brand.profileInfo.head_image_id = brandItem.value.brand_image_id
        brand.brandType = BRAND_ENUM.PROFILE
      } else if (page_type === 'PAGE_TYPE_WECHAT_OFFICIAL_ACCOUNT_DETAIL') {
        // 公众号
        brand.officialAccountInfo.promoted_object_id = page_spec.wechat_official_account_detail_spec?.app_id
        brand.brandType = BRAND_ENUM.OFFICIAL_ACCOUNT
      } else {
        // 品牌形象
        brand.brandInfo.name = brandItem.value.brand_name
        brand.brandInfo.image_id = brandItem.value.brand_image_id
        brand.brandType = BRAND_ENUM.BRAND
      }
    }
  }

  // 设置默认跳转落地页
  const pageType = ch_platform_id === PLATFORM.MINIGAME ? PAGE_TYPES.WECHAT_MINI_GAME : PAGE_TYPES.OFFICIAL
  const [textLinkItem] = creative_components?.text_link || []
  const text_link = {
    switch: false,
    value: '',
    page_type: pageType
  }
  if (textLinkItem?.value) {
    const { jump_info, link_name_type } = textLinkItem.value
    text_link.switch = true
    text_link.value = link_name_type
    text_link.page_type = jump_info.page_type
  }

  const [actionButtonItem] = creative_components?.action_button || []
  const action_button = {
    switch: false,
    value: LINK_NAME_TYPE[0].label
  }
  if (actionButtonItem?.value) {
    action_button.switch = true
    action_button.value = actionButtonItem?.value.button_text
  }

  const [miniCardLinkItem] = creative_components?.mini_card_link || []
  const mini_card_link = {
    switch: false,
    page_type: pageType,
    mini_card_link_image: {
      url: ''
    },
    mini_card_link_button_text: '',
    mini_card_link_description: ''
  }
  if (miniCardLinkItem?.value) {
    const { mini_card_link_image, mini_card_link_button_text, mini_card_link_description, jump_info } = miniCardLinkItem.value
    mini_card_link.switch = true
    mini_card_link.mini_card_link_button_text = mini_card_link_button_text
    mini_card_link.mini_card_link_description = mini_card_link_description

    if (jump_info.page_type) {
      mini_card_link.page_type = jump_info.page_type
    }

    // 单条创意修改流程
    if (material_info) {
      const imageInfo = material_info.find((v) => v.image_id === mini_card_link_image)
      if (imageInfo) {
        mini_card_link.mini_card_link_image = {
          id: imageInfo.inner_id,
          url: imageInfo.preview_url
        }
      }
    } else {
      // 批量复制、批量失败修改流程
      // 去掉 mini_card_link_image @前缀获取浮层卡片创意图片地址
      const imageDetailIds = mini_card_link_image ? mini_card_link_image.slice(1) : ''
      if (imageDetailIds) {
        const [res] = await queryImageDetailByIds({ imageDetailIds: [mini_card_link_image.slice(1)] })
        mini_card_link.mini_card_link_image = {
          ...res.items[0],
          url: res.items[0].source_url
        }
      } else {
        mini_card_link.mini_card_link_image = { url: '' }
      }
    }
  }

  const [giftCodeItem] = creative_components?.app_gift_pack_code || []
  const app_gift_pack_code = {
    switch: false,
    description: '',
    code: ''
  }
  if (giftCodeItem?.value) {
    app_gift_pack_code.switch = true
    app_gift_pack_code.code = giftCodeItem.value.code
    app_gift_pack_code.description = giftCodeItem.value.description
  }
  const [barrageItem] = creative_components?.barrage || []
  const barrage = barrageItem?.value?.list || []
  const [firstComment] = creative_components?.social_skill || []

  const social_skill = {
    social_skill_first_comment_switch: false,
    social_skill_first_comment: ''
  }

  if (firstComment?.value) {
    const social_skill_first_comment = firstComment?.value?.social_skill_first_comment || ''
    social_skill.social_skill_first_comment_switch = true
    social_skill.social_skill_first_comment = social_skill_first_comment
  }

  const [wxgamePlayableItem] = creative_components?.wxgame_playable_page || []
  let wxgame_playable_page = getWxgamePlayablePage()
  let wxgame_playable_component = getWxgamePlayableComponent()

  if (wxgamePlayableItem?.value) {
    const {
      wxgame_playable_page_switch,
      wxgame_playable_page_trigger_types,
      wxgame_playable_page_card_link_image,
      wxgame_playable_page_end_cover_img
    } = wxgamePlayableItem.value

    // 通过参数：wxgame_playable_page_trigger_types 来判定是组件化创意的营销组件试玩或是自定义创意的试玩
    const isWxgamePlayableComponent = !!wxgame_playable_page_trigger_types

    if (isWxgamePlayableComponent) {
      wxgame_playable_component = {
        ...wxgame_playable_component,
        ...wxgamePlayableItem.value,
        switch: wxgame_playable_page_switch
      }

      if (material_info) {
        const cardImageInfo = material_info.find((v) => v.image_id === wxgamePlayableItem.value.wxgame_playable_page_card_link_image)
        const endImageInfo = material_info.find((v) => v.image_id === wxgamePlayableItem.value.wxgame_playable_page_end_cover_img)
        if (cardImageInfo) {
          wxgame_playable_component.card_link_image = {
            id: cardImageInfo.inner_id,
            url: cardImageInfo.preview_url,
            height: cardImageInfo.height,
            width: cardImageInfo.width
          }
        }

        if (endImageInfo) {
          wxgame_playable_component.end_cover_img = {
            id: endImageInfo.inner_id,
            url: endImageInfo.preview_url,
            height: endImageInfo.height,
            width: endImageInfo.width
          }
        }
      } else {
        // 批量复制、批量失败修改流程
        // 去掉 wxgame_playable_page_card_link_image @前缀获取试玩图片地址
        // 去掉 wxgame_playable_page_end_cover_img @前缀获取试玩结束页图片地址
        const imageDetailIds = [wxgame_playable_page_card_link_image, wxgame_playable_page_end_cover_img].map((i) => Number(i.slice(1)))
        const imageRes = await queryImageDetailByIds({ imageDetailIds })
        // 最终结果根据imageDetailIds再排序
        const [cardImageRes, endImageRes] = imageRes.sort(
          (a, b) => imageDetailIds.indexOf(a.items[0].id) - imageDetailIds.indexOf(b.items[0].id)
        )
        if (cardImageRes && cardImageRes.items) {
          wxgame_playable_component.card_link_image = { ...cardImageRes.items[0], url: cardImageRes.items[0].source_url }
        }
        if (endImageRes && endImageRes.items) {
          wxgame_playable_component.end_cover_img = { ...endImageRes.items[0], url: endImageRes.items[0].source_url }
        }
      }
    } else {
      wxgame_playable_page = {
        ...wxgame_playable_page,
        ...wxgamePlayableItem.value
      }
      if (material_info) {
        const imageInfo = material_info.find((v) => v.image_id === wxgamePlayableItem.value.wxgame_playable_page_end_cover_img)
        if (imageInfo) {
          wxgame_playable_page.wxgame_playable_page_end_cover_img = {
            id: imageInfo.inner_id,
            url: imageInfo.preview_url,
            height: imageInfo.height,
            width: imageInfo.width
          }
        }
      } else {
        // 批量复制、批量失败修改流程
        // 去掉 wxgame_playable_page_end_cover_img @前缀获取试玩结束页创意图片地址
        const [res] = await queryImageDetailByIds({
          imageDetailIds: [wxgamePlayableItem.value.wxgame_playable_page_end_cover_img.slice(1)]
        })

        if (res && res.items) {
          wxgame_playable_page.wxgame_playable_page_end_cover_img = { ...res.items[0], url: res.items[0].source_url }
        }
      }
    }
  }
  const [floatingZoneItem] = creative_components?.floating_zone || []
  let floating_zone = {
    floating_zone_switch: false,
    floating_zone_image: {},
    floating_zone_name: '',
    floating_zone_desc: '',
    floating_zone_button_text: '',
    floating_zone_type: 'FLOATING_ZONE_TYPE_IMAGE_TEXT'
  }

  if (floatingZoneItem?.value) {
    floating_zone = {
      ...floating_zone,
      ...floatingZoneItem.value
    }

    // 单条创意修改流程
    if (material_info) {
      const imageInfo = material_info.find((v) => v.image_id === floatingZoneItem.value.floating_zone_image_id)
      if (imageInfo) {
        floating_zone.floating_zone_image = {
          id: imageInfo.inner_id,
          url: imageInfo.preview_url
        }
      }
    } else {
      // 批量复制、批量失败修改流程
      // 去掉 floating_zone_image_id @前缀获取浮层卡片创意图片地址
      const [res] = await queryImageDetailByIds({ imageDetailIds: [floatingZoneItem.value.floating_zone_image_id.slice(1)] })

      // res 可能为 undefined（接口返回空数组），兜底避免 res.items 崩溃，与同文件 755/784 行判空一致
      if (res && res.items) {
        floating_zone.floating_zone_image = {
          ...res.items[0],
          url: res.items[0].source_url
        }
      }
    }
  }

  const [showDataItem] = creative_components?.show_data || []
  const show_data = {
    switch: false,
    conversion_data_type: 'CONVERSION_DATA_ADMETRIC'
  }
  if (showDataItem?.value) {
    show_data.switch = true
    show_data.conversion_data_type = showDataItem.value.conversion_data_type
    show_data.conversion_target_type = showDataItem.value.conversion_target_type
  }

  const [labelItem] = creative_components?.label || []
  const label = labelItem?.value ? labelItem.value.list.map((ret) => ({ name: ret.content })) : []

  const mainJumpInfoItem = creative_components?.main_jump_info?.map((info) => info.value) || []
  const pageInfo = mainJumpInfoItem.map((item) => {
    // 3:appstore 2:xijing 4:小游戏
    const { xj_quick_spec, official_spec } = item.page_spec || {}
    const { page_id } = xj_quick_spec || official_spec || {}
    let pageType = ''
    if (xj_quick_spec && page_id) pageType = 2
    if (official_spec && page_id) pageType = 5

    if (PLATFORM.IOS === ch_platform_id && !page_id) {
      pageType = 3
    }

    if (PLATFORM.MINIGAME === ch_platform_id && !page_id) {
      pageType = 4
    }

    return {
      page: { page_id: page_id ? String(page_id) : '' },
      pageType
    }
  })

  const end_page = {
    switch: false,
    end_page_type: 'END_PAGE_AVATAR_NICKNAME_HIGHLIGHT', // END_PAGE_AVATAR_NICKNAME_HIGHLIGHT 突出头像昵称 ， END_PAGE_DESCRIPTION_HIGHLIGHT 突出文案
    end_page_desc: ''
  }
  const [endPage] = creative_components?.end_page || []

  if (endPage?.value) {
    end_page.switch = true
    end_page.end_page_type = endPage.value.end_page_type
    end_page.end_page_desc = endPage.value.end_page_desc
  }
  const creativeForm = {
    delivery_mode,
    dynamic_creative_type,
    groupName: ideaName,
    dynamic_creative_name,
    creative_template_id,
    selectedAssets: {},
    description: description?.map((desc) => ({ content: desc.value.content })) || [],
    components: {
      brand,
      text_link,
      action_button,
      mini_card_link,
      app_gift_pack_code,
      label,
      floating_zone,
      wxgame_playable_page,
      wxgame_playable_component,
      social_skill,
      barrage,
      show_data,
      end_page,
      wechat_channels
    },
    pageInfo
  }
  // 单条广告计划修改流程获取回显的素材数据
  if (material_info) {
    // 视频
    const videoIds = materialCreativeComponents.video?.map((v) => v.value.video_id) || []
    // 图片
    const imageIds = materialCreativeComponents.image?.map((v) => v.value.image_id) || []
    // 组图
    const imageGroup = materialCreativeComponents.image_list?.map((v) => v.value.list) || []
    const videoItems = []
    const imageItems = []

    const grouoImageIds = imageGroup.map((group) => {
      return group.map((i) => i.image_id).join(',')
    })
    const imageListItems = grouoImageIds.map((ids) => {
      const material = material_info.find((m) => String(m.image_id) === ids)
      const imageList = material.image_id.split(',').map(String)
      // 为单条修改添加image_list_urls，构造与新建流程一致的参数
      // src/views/create/gdt/model/distribute-form.js 需要去url的md5值
      const image_list_urls = material_info
        .filter((m) => imageList.includes(m.image_id))
        // 组图须根据原始顺序排序，不然会组图顺序错乱，导致匹配子素材失败
        .sort((m1, m2) => imageList.indexOf(m1.image_id) - imageList.indexOf(m2.image_id))
        // 模拟一个图片链接，最终取的是地址的md5值
        .map((m) => `https://ptom-resource-cos.caohua.com/ptom/advResource/video/213876/${m.signature}.jpg`)

      return {
        ...externalDataFormatter.group_image(material),
        image_list_urls
      }
    })

    videoIds.forEach((videoId) => {
      const material = material_info.find(
        (item) => normalizeCreativeMaterialId(item.video_id) === normalizeCreativeMaterialId(videoId)
      )
      if (material) videoItems.push(externalDataFormatter.video(material))
    })

    imageIds.forEach((imageId) => {
      const material = material_info.find(
        (item) => normalizeCreativeMaterialId(item.image_id) === normalizeCreativeMaterialId(imageId)
      )
      if (material) imageItems.push(externalDataFormatter.image(material))
    })

    creativeForm.selectedAssets.video = videoItems
    creativeForm.selectedAssets.image = imageItems
    creativeForm.selectedAssets.image_list = imageListItems
  } else {
    const { video, image, image_list } = await getCreativeAssets(materialCreativeComponents, materials)
    if (video) creativeForm.selectedAssets.video = video
    if (image) creativeForm.selectedAssets.image = image
    if (image_list) creativeForm.selectedAssets.image_list = image_list
  }

  return [creativeForm]
}

/**
 * @description: 获取素材详情
 * @param {*} creative_components
 * @param {*} materials
 * @return {*}
 */
async function getCreativeAssets(creative_components, materials) {
  const results = {}
  // 批量复制、批量失败修改流程获取回显的素材数据
  const videoIds = creative_components.video?.map((v) => normalizeCreativeMaterialId(v.value.video_id)) || []
  const imageIds = creative_components.image?.map((v) => normalizeCreativeMaterialId(v.value.image_id)) || []
  let imageListIds = new Set()
  for (let i = 0; i < materials.length; i++) {
    const material = materials[i]
    if (material.type === 'GROUP_IMAGE') {
      imageListIds.add(material.inner_id)
    }
  }
  imageListIds = Array.from(imageListIds)

  // 视频
  if (videoIds?.length) {
    const videos = []
    for (let i = 0; i < creative_components.video.length; i++) {
      let { video_id, cover_id } = creative_components.video[i].value
      // 从提交参数materials中获取当前视频封面是否是默认封面
      const coverMetaData = materials.find((m) => m.key === cover_id) || {}
      video_id = normalizeCreativeMaterialId(video_id)
      cover_id = normalizeCreativeMaterialId(cover_id)
      const { defaultCover } = coverMetaData
      const getDetailParams = defaultCover
        ? { imageDetailIds: [video_id], frameIds: [cover_id] } // 默认封面获取详情
        : { imageDetailIds: [video_id, cover_id] } // 图片封面获取详情

      const details = await queryImageDetailByIds(getDetailParams)
      const video = details.find((detail) => detail.items[0].id === Number(video_id))
      const cover = details.find((detail) => detail.items[0].id === Number(cover_id))
      if (video && cover) {
        // 使用获取到的封面数据替换视频内的封面数据
        video.items[0].coverId = cover.items[0].id
        video.items[0].replaced_cover_url = cover.items[0].cover_url
        video.items[0].cover_width = cover.items[0].width
        video.items[0].cover_height = cover.items[0].height
        video.items[0].cover_size = cover.items[0].size
        videos.push(video)
      }
    }
    results.video = videos.map((v) => dataFormatter.video(v.items[0]))
  }

  // 图片
  if (imageIds?.length) {
    const images = await queryImageDetailByIds({ imageDetailIds: imageIds })
    results.image = imageIds
      .map((imageId) => images.find((item) => String(item.items[0].id) === String(imageId)))
      .filter(Boolean)
      .map((item) => dataFormatter.image(item.items[0]))
  }

  // 组图
  if (imageListIds?.length) {
    const imageLists = await queryImageDetailByIds({ imageDetailIds: imageListIds })
    const image_list =
      imageLists?.map((i) => {
        const [image] = i.items
        return {
          ...dataFormatter.group_image(image)
        }
      }) || []
    results.image_list = image_list
  }
  return results
}

function rebuildPlanBudgetForm(taskInfo, bidAdjustmentOptions) {
  const {
    begin_date,
    end_date,
    time_series,
    daily_budget,
    total_budget,
    deep_conversion_spec,
    search_expand_targeting_switch,
    custom_cost_cap,
    custom_cost_roi_cap,
    smart_delivery_scene_spec,
    smart_delivery_platform,
    bid_amount,
    deep_conversion_worth_rate
  } = taskInfo.outer_plan_params

  const { smart_delivery_goal, smart_delivery_goal_spec, conversion_id_list } = smart_delivery_scene_spec || {}
  const params = {
    ...pick(taskInfo.outer_plan_params, Object.keys(budgetForm)),
    ...rebuildBidAdjustmentForm(taskInfo.outer_plan_params.bid_adjustment, bidAdjustmentOptions)
  }

  // 如果结束日期不为空，设置日期类型
  if (end_date) {
    params.dateRange = 2
    params.rangeDate = [begin_date, end_date]
  }

  // 指定投放时段
  if (time_series.includes('0')) {
    params.timeRange = 1
  }
  // 是否设置日预算
  if (daily_budget && daily_budget > 0) {
    params.budget = 1
  }
  if (total_budget && total_budget > 0) {
    params.totalBudget = 1
  }

  if (deep_conversion_spec?.deep_conversion_type) {
    params.deep_bid_mode = true
    const { deep_conversion_type } = deep_conversion_spec
    // 如果当前是优化ROI，表单追加优化转化行为的参数
    if (deep_conversion_type === DEEP_CONVERSION_TYPE.WORTH) {
      deep_conversion_spec.deep_conversion_behavior_spec = clone(deepConversionBehavior)

      params.custom_cost_cap = custom_cost_roi_cap
    }

    // 如果当前是优化转化行为，表单追加优化ROI的参数
    if (deep_conversion_type === DEEP_CONVERSION_TYPE.BEHAVIOR) {
      deep_conversion_spec.deep_conversion_worth_spec = clone(deepConversionRoi)
      params.custom_cost_cap = custom_cost_cap
    }
    params.deep_conversion_spec = deep_conversion_spec
  } else {
    // 使用默认深度转化参数
    params.deep_conversion_spec = clone(budgetForm.deep_conversion_spec)
  }
  if (search_expand_targeting_switch === SEARCH_EXPAND_TARGETING_SWITCH.OPEN) {
    params.search_expansion_switch = SEARCH_EXPANSION_SWITCH.OPEN
    params.search_expand_targeting_switch = search_expand_targeting_switch
  }

  if (smart_delivery_scene_spec) {
    params.smart_delivery_goal = smart_delivery_goal

    if (smart_delivery_goal_spec && typeof smart_delivery_goal_spec === 'object') {
      params.conversion_id_list = conversion_id_list

      if (smart_delivery_platform === SMART_DELIVERY_PLATFORM.MINI_GAME_PROMOTION) {
        if (smart_delivery_goal_spec.mini_game_promotion_spec) {
          const { register_cost, seven_day_purchase_roi, day30_purchase_roi, first_day_purchase_roi } =
            smart_delivery_goal_spec.mini_game_promotion_spec

          const defaultSmartPromotionSpec = clone(budgetForm.smart_promotion_spec)
          switch (smart_delivery_goal) {
            case SMART_DELIVERY_GOAL.FIRST_DAY_PURCHASE_ROI:
              params.smart_promotion_spec = {
                ...defaultSmartPromotionSpec,
                register_cost,
                first_day_purchase_roi
              }
              break
            case SMART_DELIVERY_GOAL.SEVEN_DAY_PURCHASE_ROI:
              params.smart_promotion_spec = {
                ...defaultSmartPromotionSpec,
                register_cost,
                seven_day_purchase_roi
              }
              break
            case SMART_DELIVERY_GOAL.DAY30_PURCHASE:
              params.smart_promotion_spec = {
                ...defaultSmartPromotionSpec,
                register_cost,
                day30_purchase_roi
              }
              break
            default:
              break
          }
        }

        if (smart_delivery_goal_spec.mix_iaap_in_one_spec) {
          const { iaap_roi, iaap_mix_in_one_factor } = smart_delivery_goal_spec.mix_iaap_in_one_spec
          params.smart_promotion_spec = {
            ...clone(budgetForm.smart_promotion_spec),
            iaap_roi,
            iaap_mix_in_one_factor
          }
        }

        if (smart_delivery_goal_spec.bid_amount_deep_conversion_worth_rate_spec) {
          const { bid_amount, deep_conversion_worth_rate } = smart_delivery_goal_spec.bid_amount_deep_conversion_worth_rate_spec
          params.bid_amount_deep_conversion_worth_rate_spec = { bid_amount, deep_conversion_worth_rate }
        }
      }
      if (smart_delivery_platform === SMART_DELIVERY_PLATFORM.GAME_APP) {
        if (smart_delivery_goal === SMART_DELIVERY_GOAL.GAME_APP_ACTIVATE_ROI_1DAY) {
          const { bid_amount, deep_conversion_worth_rate } = smart_delivery_goal_spec.game_app_activate_roi_spec
          params.game_app_activate_roi_spec = { bid_amount, deep_conversion_worth_rate }
        }

        if (
          [SMART_DELIVERY_GOAL.ACTIVATE, SMART_DELIVERY_GOAL.FIRST_PURCHASE, SMART_DELIVERY_GOAL.APP_PURCHASE].includes(smart_delivery_goal)
        ) {
          const { bid_amount = '' } = smart_delivery_goal_spec.bid_amount_spec
          params.bid_amount_spec = { bid_amount }
        }
      }
    }
  }

  // 变现ROI 系列回显：对齐腾讯 3.0，不传 smart_delivery_scene_spec，按 deep_conversion_worth_spec.goal 判定
  // roi 值在提交快照里存于 deep_conversion_worth_spec.expected_roi（spec 内）
  const monetizationGoalValues = [
    SMART_DELIVERY_GOAL.FIRST_DAY_MONETIZATION,
    SMART_DELIVERY_GOAL.SEVEN_DAY_MONETIZATION,
    SMART_DELIVERY_GOAL.APP_REGISTER_30DAY_MONETIZATION_ROAS
  ]
  const deepWorthSpec = deep_conversion_spec?.deep_conversion_worth_spec
  const worthGoal = deepWorthSpec?.goal
  if (worthGoal && monetizationGoalValues.includes(worthGoal)) {
    params.smart_delivery_platform = smart_delivery_platform
    params.smart_delivery_goal = worthGoal
    params.bid_amount_spec = { bid_amount: bid_amount ?? '' }
    params.smart_promotion_spec = {
      ...clone(budgetForm.smart_promotion_spec),
      monetization_roi: deepWorthSpec?.expected_roi ?? deep_conversion_worth_rate ?? ''
    }
  }

  return params
}

function rebuildTargetingForm(taskInfo) {
  const { outer_plan_params, ad_account_id } = taskInfo
  const {
    targetingName,
    age,
    gender,
    geo_location,
    custom_audience,
    excluded_custom_audience,
    device_brand_model,
    user_os,
    network_type,
    game_consumption_level,
    wechat_ad_behavior,
    device_price,
    excluded_converted_audience
  } = outer_plan_params.targeting

  const params = {
    targetingName
  }

  // 人群包
  if (custom_audience || excluded_custom_audience) {
    const populations = {
      [ad_account_id]: {
        includes: custom_audience || [],
        excludes: excluded_custom_audience || []
      }
    }
    params.audienceType = 1
    params.populations = populations
  } else {
    params.populations = getTargetPopulations([{ id: ad_account_id }])
  }

  // 年龄
  if (age) {
    const { min, max } = age[0]
    const ageItem = AGES.find((a) => {
      const arr = a.value.split(',').map(Number)
      return arr[0] === min && arr[1] === max
    })
    if (ageItem) {
      params.age = ageItem.value
    } else {
      // 自定义年龄段
      params.age = 1
      params.ageRange = [min, max]
    }
  }

  // 性别
  if (gender) {
    params.gender = gender[0]
  }

  // 地域
  if (geo_location) {
    params.regionType = 1
    params.region = geo_location.regions
    params.locationTypes = geo_location.location_types
  }

  // 手机品牌
  if (device_brand_model) {
    const { included_list = [], excluded_list = [] } = device_brand_model
    params.deviceType = 1
    if (excluded_list?.length) {
      params.excludeDevice = true
    }
    params.device = included_list.concat(excluded_list)
  }

  // 操作系统
  if (user_os) {
    params.osType = 1
    params.version = user_os
  }

  // 网络
  if (network_type) {
    params.network = network_type
  }

  // 游戏消费能力
  if (game_consumption_level) {
    params.game_consumption_level = game_consumption_level || [0]
  }

  // 微信再营销
  if (wechat_ad_behavior) {
    const { actions, excluded_actions, mini_game_wechat_registered_activity = 'THIRTY_DAYS_NO_ACTIVE' } = wechat_ad_behavior
    const wechatAdBehaviorType = []
    const wechatAdBehavior = {
      actions: [],
      excluded_actions: [],
      mini_game_wechat_registered_activity
    }
    if (actions?.length) {
      wechatAdBehaviorType.push('ACTIONS')
      wechatAdBehavior.actions = actions
    }
    if (excluded_actions?.length) {
      wechatAdBehaviorType.push('EXCLUDED_ACTIONS')
      wechatAdBehavior.excluded_actions = excluded_actions
    }
    params.wechatAdBehavior = wechatAdBehavior
    params.wechatAdBehaviorType = wechatAdBehaviorType
  }

  // 如果没有微信再营销的字段或者，排除和包含都为空时，设置为不限
  const { actions, excluded_actions } = params.wechatAdBehavior || {}
  if (!wechat_ad_behavior || (!actions?.length && !excluded_actions?.length)) {
    params.wechatAdBehaviorType = [0]
  }

  if (device_price) {
    // 设备价格
    params.devicePrice = device_price
  }

  if (excluded_converted_audience) {
    // 排除已转化用户
    const { excluded_dimension = 0, conversion_behavior_list, excluded_day } = excluded_converted_audience
    params.excluded_converted_audience = {
      excluded_dimension,
      conversion_behavior_list: conversion_behavior_list?.length ? conversion_behavior_list : [CONVERSION_BEHAVIOR.FOLLOW],
      excluded_day
    }
    if (conversion_behavior_list?.length) {
      params.excludeCustom = true
    }
  }

  return params
}

async function rebuildCreativeForm(taskInfo) {
  const { adcreative_elements_array, ideaName, ...rest } = taskInfo.creative_params

  const [adcreative_elements] = adcreative_elements_array
  const { dynamic_creative_elements, image_covers } = adcreative_elements
  const {
    brand,
    button_text,
    description_options,
    video_options,
    image_options,
    floating_zone_struct,
    end_page,
    wechat_channels_spec,
    finder_object_visibility = false,
    label
  } = dynamic_creative_elements

  const params = {
    wechat_page: 2,
    ...rest,
    groupName: ideaName
  }
  const creativeFormModel = {
    button_text,
    description_options: description_options.map((desc) => ({ text: desc })),
    end_page,
    finder_object_visibility,
    label: {
      content: label?.length ? label.map((l) => l.content).join(',') : ''
    }
  }

  if (wechat_channels_spec) {
    creativeFormModel.wechat_channels_spec = wechat_channels_spec
  }

  if (brand) {
    // 根据id获取品牌形象图片地址
    let [brandDetails] = await queryImageDetailByIds({ imageDetailIds: [brand.brand_img] })
    brandDetails = dataFormatter.logo(brandDetails.items[0])
    creativeFormModel.brand = {
      ...brandDetails,
      brand_name: brand.brand_name,
      brand_img: brandDetails.preview_url
    }
  }

  // 根据id获取非视频素材图片地址
  if (image_options?.length && !video_options?.length) {
    const [imageDetails] = await queryImageDetailByIds({ imageDetailIds: image_options })
    if (imageDetails) {
      creativeFormModel.image_options = imageDetails.items.map((row) => {
        return dataFormatter.image(row)
      })
    }
  }

  if (floating_zone_struct) {
    creativeFormModel.floating_zone_struct = {
      button_text: floating_zone_struct.floating_zone_button_text,
      desc: floating_zone_struct.floating_zone_desc,
      name: floating_zone_struct.floating_zone_name,
      floating_zone_switch: floating_zone_struct.floating_zone_switch,
      type: floating_zone_struct.floating_zone_type
    }
    //  根据浮层卡片图片id获取图片地址
    let [floatingZoneDetails] = await queryImageDetailByIds({ imageDetailIds: [floating_zone_struct.floating_zone_image_id] })
    if (floatingZoneDetails) {
      floatingZoneDetails = dataFormatter.image(floatingZoneDetails.items[0])
      creativeFormModel.floating_zone_struct.floatingZoneImage = floatingZoneDetails
      creativeFormModel.floating_zone_struct.image_id = floatingZoneDetails.preview_url
    }
  }

  // 根据id获取视频及封面，封面须区分视频封面和图片封面
  if (video_options?.length) {
    // 找到全部的图片封面id
    const imageCovers = Object.keys(image_covers).map((id) => Number(id))
    // 用视频封面列表image_options比对不在imageCovers中的视频封面
    const videoCovers = image_options.filter((id) => !imageCovers.includes(id))

    // 再获取视频和封面详情
    // 同时获取图片封面和视频素材
    const imageDetailIds = imageCovers.concat(video_options)
    // 视频原始封面用frameIds获取
    const videoDetails = await queryImageDetailByIds({ imageDetailIds, frameIds: videoCovers })

    const videoSources = []
    const imageCoverSources = []
    const videoCoverSources = []
    videoDetails.forEach((d) => {
      const [item] = d.items
      if (imageCovers.includes(item.id)) {
        imageCoverSources.push(item)
      }

      if (videoCovers.includes(item.id)) {
        videoCoverSources.push(item)
      }

      if (video_options.includes(item.id)) {
        videoSources.push(item)
      }
    })

    // 根据video_options与image_options在数组中索引的对应关系,替换视频素材中的封面id、url、width、height
    for (let i = 0; i < video_options.length; i++) {
      const videoId = video_options[i]

      const coverId = image_options[i]
      const cover = imageCoverSources.concat(videoCoverSources).find((c) => c.id === coverId)

      const idx = videoSources.findIndex((video) => video.id === videoId)
      videoSources.splice(idx, 1, {
        ...videoSources[idx],
        cover_url: cover.cover_url,
        coverId: cover.id,
        cover_height: cover.height,
        cover_width: cover.width,
        cover_size: cover.size
      })
    }

    creativeFormModel.video_options = videoSources.map((video) => {
      return {
        // imageCover 标记是否图片素材封面，在/model/distribute-form.js使用并传到后端
        // 会在/components/creative/template-contents.vue 中选择视频封面时被修改
        imageCover: imageCoverSources.map((i) => i.id).includes(video.coverId),
        ...dataFormatter.video(video)
      }
    })
  }

  params.creativeFormModel = creativeFormModel

  // 行动按钮
  // if (params.link_name_type) {
  //   this.creativeCategoryForm = {
  //     link_name_type: params.link_name_type
  //   }
  // }

  // 获取落地页
  const { page_spec } = params
  const { page_id } = page_spec || {}

  params.pageInfo = {
    pageType: params.wechat_page || 2,
    page_spec: {
      page_id: page_id || ''
    }
  }

  // 头像昵称
  const { profile_id } = params
  params.profile = {
    profile_id: profile_id || ''
  }
  return params
}

/**
 * @description: 根据任务参数回显腾讯广告3.0批量复制、批量修改流程
 * @param {*} taskInfo
 */
export async function getV3ModifyPlanForm(taskInfo) {
  const { taskId, task_progress, task_status, convert_name, ad_account_name, ad_account_id, batch_task_type, operation } = taskInfo
  const { ad_marketing_scene } = taskInfo.outer_plan_params
  const isSearchAd = ad_marketing_scene === AD_MARKETING_SCENE.VERSION_SEARCH
  const planForm = {
    taskId,
    task_progress,
    task_status,
    campaign_type: 0,
    convert_name,
    batch_task_type,
    ad_account_name,
    ad_account_id,
    planForm: {
      // 项目默认参数
      ...getV3PlanForm(),
      // 根据任务参数构造的项目参数
      ...(isSearchAd ? rebuildSearchAdPlanForm.call(this, taskInfo) : rebuildV3PlanForm.call(this, taskInfo)),
      operation: taskInfo.outer_plan_params.operation || 'disable'
    },
    budgetForm: {
      // 深拷贝默认表单，避免多任务共享同一嵌套对象引用（smart_promotion_spec/bid_amount_spec 等）
      ...clone(budgetForm),
      // 根据任务参数构造的项目出价参数
      ...rebuildV3PlanBudgetForm(taskInfo, { clearOnDisable: this.pageStates.isModify })
    },
    targetingForm: {
      // 默认定向参数
      ...getTargetingForm.call(this),
      // 根据任务参数构造的定向参数
      ...rebuildTargetingForm(taskInfo)
    },
    creativeFormGroups: isSearchAd ? await rebuildSearchAdCreativeForm(taskInfo) : await rebuildV3CreativeForm(taskInfo)
  }

  return planForm
}

/**
 * @description: 获取腾讯广告3.0单条计划媒体参数
 * @return {*}
 */
export function getV3UpdatePlanForm(data) {
  const { out_plan_info, plan_info } = data
  const {
    adgroup_name,
    marketing_goal,
    marketing_sub_goal,
    marketing_carrier_detail,
    marketing_carrier_type,
    scene_spec,
    site_set = MANUAL_SITES,
    smart_delivery_platform,
    smart_delivery_scene_spec,
    automatic_site_enabled,
    exploration_strategy,
    smart_targeting_mode
  } = out_plan_info
  const promoted_object_type = PROMOTED_TYPE_BY_PLATFORM[plan_info.platform]
  const { wechat_channels_scene, wechat_position, wechat_scene, pc_scene } = scene_spec || {}
  const planForm = {
    accounts: [plan_info.adAccountId],
    project_name: adgroup_name,
    ch_platform_id: plan_info.platform,
    game_id: {
      id: plan_info.gameId
    },
    callback_switch: plan_info.callbackSwitch,
    monitorPlan: [],
    automatic_site_enabled,
    exploration_strategy,
    siteSet: site_set,

    promotedObject: {
      promoted_object_type,
      promoted_object_id: marketing_carrier_detail.marketing_carrier_id
    },

    wechat_position: wechat_position?.length ? 1 : 0,
    wechatPostion: wechat_position || [],
    // 微信视频号定投
    wechat_channels: wechat_channels_scene?.length ? 1 : 0,
    wechatChannels: wechat_channels_scene || [],
    // 微信公众号与小程序场景
    wechat_scene: 0,
    // 公众号媒体类型
    officialAccountMediaCate: [],
    miniGame: [], // 小程序小游戏流量 checkbox model
    payScene: [], // 订单详情页消费场景 checkbox model

    pcScene: pc_scene || [],
    pc_scene: pc_scene?.length ? 1 : 0,

    displaySceneType: 0, // 优量汇广告展示场景
    excludeUnionType: 0, // 优量汇流量包屏蔽
    flow_optimization_enabled: false, // 优量汇自动流量优选
    scene_spec: {
      display_scene: [],
      // 分帐号保存的已选优量汇流量包屏蔽包
      // {account_id: 0, excludePackages: []}
      exclude_union_position_package: {}
    },

    marketing_goal: marketing_goal,
    marketing_sub_goal: marketing_sub_goal,
    marketing_carrier_type: marketing_carrier_type,
    marketing_carrier_detail: {
      marketing_carrier_id: marketing_carrier_detail.marketing_carrier_id
    },

    // 定向模式回显
    smart_targeting_mode: smart_targeting_mode === 'SMART_TARGETING_MANUAL'
  }

  if (wechat_scene) {
    const { official_account_media_category, mini_program_and_mini_game, pay_scene } = wechat_scene
    planForm.wechat_scene = 1
    planForm.officialAccountMediaCate = official_account_media_category
    planForm.miniGame = mini_program_and_mini_game
    planForm.payScene = pay_scene
  }

  // 智投广告
  if (smart_delivery_platform && smart_delivery_scene_spec) {
    planForm.ad_marketing_scene = AD_MARKETING_SCENE.VERSION_SMART
    planForm.smart_delivery_platform = smart_delivery_platform

    // 智投如果没返回手动版位自动版位信息，那么默认开启自动版位
    if (typeof planForm.automatic_site_enabled === 'undefined') {
      planForm.automatic_site_enabled = true
      planForm.exploration_strategy = EXPLORATION_ENUM.AUTOMATIC
    }

    const { smart_delivery_goal, smart_delivery_goal_spec, conversion_id_list } = smart_delivery_scene_spec
    const { mini_game_promotion_spec, mix_iaap_in_one_spec, bid_amount_deep_conversion_worth_rate_spec } = smart_delivery_goal_spec

    planForm.conversion_id_list = conversion_id_list

    if (mini_game_promotion_spec) {
      const { register_cost, seven_day_purchase_roi, first_day_purchase_roi } = mini_game_promotion_spec
      const defaultSmartPromotionSpec = clone(budgetForm.smart_promotion_spec)
      switch (smart_delivery_goal) {
        case SMART_DELIVERY_GOAL.FIRST_DAY_PURCHASE_ROI:
          planForm.smart_promotion_spec = {
            ...defaultSmartPromotionSpec,
            register_cost,
            first_day_purchase_roi
          }
          break
        case SMART_DELIVERY_GOAL.SEVEN_DAY_PURCHASE_ROI:
          planForm.smart_promotion_spec = {
            ...defaultSmartPromotionSpec,
            register_cost,
            seven_day_purchase_roi
          }
          break
        default:
          break
      }
    }

    if (mix_iaap_in_one_spec) {
      const { iaap_roi, iaap_mix_in_one_factor } = mix_iaap_in_one_spec
      planForm.smart_promotion_spec = {
        ...clone(budgetForm.smart_promotion_spec),
        iaap_roi,
        iaap_mix_in_one_factor
      }
    }

    if (bid_amount_deep_conversion_worth_rate_spec) {
      const { bid_amount, deep_conversion_worth_rate } = bid_amount_deep_conversion_worth_rate_spec
      planForm.bid_amount_deep_conversion_worth_rate_spec = {
        ...clone(budgetForm.bid_amount_deep_conversion_worth_rate_spec),
        bid_amount,
        deep_conversion_worth_rate
      }
    }
  } else {
    planForm.ad_marketing_scene = AD_MARKETING_SCENE.VERSION_3
  }
  return planForm
}

/**
 * @description: 获取腾讯广告3.0单条修改定向参数
 * @param {*} data
 */
export function getV3UpdateTargetingForm(data) {
  const { targeting } = data.out_plan_info
  const params = {
    // 默认参数
    ...getTargetingForm.call(this),
    // 根据计划参数回显
    ...rebuildTargetingForm({ outer_plan_params: { targeting }, ad_account_id: data.plan_info.adAccountId })
  }
  return params
}

/**
 * @description: 获取腾讯广告3.0单条修改出价参数
 * @param {*} data
 */
export function getV3UpdateBudgetForm(data) {
  const params = {
    ...budgetForm,
    ...rebuildPlanBudgetForm({ outer_plan_params: data.out_plan_info }, { clearOnDisable: true })
  }
  return params
}

export async function getV3UpdateCreativeForm(data) {
  const { creative_info, material_info, platform } = data
  const params = await rebuildV3CreativeForm({
    creative_params: creative_info,
    material_info: material_info,
    inner_plan_params: { ch_platform_id: platform }
  })
  return params
}

export default {
  getTargetingForm
}
